import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useState } from "react";

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const categoryLabels = {
  parsing_readability: "Parsing & readability",
  structure: "Structure",
  formatting: "Formatting",
  content_strength: "Content strength",
  completeness: "Completeness",
};

function scoreTone(score) {
  if (score >= 80) return "#0b735d";
  if (score >= 60) return "#b36b00";
  return "#a63d22";
}

export default function AtsChecker() {
  const [cvFile, setCvFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [consent, setConsent] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setReport(null);

    if (!cvFile) {
      setError("Choose a PDF, DOCX, DOC or XML CV first.");
      return;
    }
    if (!consent) {
      setError("Please confirm that you are allowed to upload this CV for a one-time assessment.");
      return;
    }
    if (cvFile.size > 5 * 1024 * 1024) {
      setError("Please choose a CV that is 5MB or smaller.");
      return;
    }

    const formData = new FormData();
    formData.append("cv", cvFile);
    if (jobDescription.trim()) formData.append("job_description", jobDescription.trim());

    setIsSubmitting(true);
    try {
      const response = await fetch(API_BASE_URL + "/api/ats/assess", {
        method: "POST",
        body: formData,
      });
      const responseText = await response.text();
      let body = {};
      try {
        body = responseText ? JSON.parse(responseText) : {};
      } catch {
        body = {};
      }
      if (!response.ok) {
        throw new Error(body.detail || "The CV assessment service returned " + response.status + ". Please try again in a moment.");
      }
      setReport(body);
    } catch (submissionError) {
      setError(submissionError.message || "We could not assess this CV. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }


  const fallbackPlan = report ? [
    {
      key: "parsing_readability",
      title: "Improve readability",
      current: report.category_scores?.parsing_readability || 0,
      maximum: 25,
      actions: ["Use selectable text rather than an image-only CV.", "Keep contact details and the main CV content as normal body text."]
    },
    {
      key: "structure",
      title: "Strengthen the CV structure",
      current: report.category_scores?.structure || 0,
      maximum: 20,
      actions: ["Use clear sections such as Profile, Experience, Skills and Education.", "Give recent roles clear dates, job titles and employer names."]
    },
    {
      key: "formatting",
      title: "Simplify the formatting",
      current: report.category_scores?.formatting || 0,
      maximum: 20,
      actions: ["Keep essential information out of headers, footers, text boxes and images so it can be read reliably.", "Use consistent headings and bullets, and remove any repeated lines.", "Save an editable DOCX or text-based PDF before checking again."]
    },
    {
      key: "content_strength",
      title: "Make experience evidence stronger",
      current: report.category_scores?.content_strength || 0,
      maximum: 20,
      actions: ["Where truthful, add the outcome or scale of your work, such as volume handled, accuracy, people supported, time saved or results achieved — never invent figures.", "Start experience bullets with clear action verbs and connect them to the skills requested in the role.", "Tailor the strongest examples to the job advert before re-checking."]
    },
    {
      key: "completeness",
      title: "Complete key CV details",
      current: report.category_scores?.completeness || 0,
      maximum: 15,
      actions: ["Include a contact method, short profile, relevant experience, skills and education or training."]
    }
  ].filter((item) => item.current < item.maximum) : [];

  const improvementPlan = report && Array.isArray(report.improvement_plan) && report.improvement_plan.length > 0 ? report.improvement_plan : fallbackPlan;

  return (
    <main>
      <section className="section" style={{ paddingTop: "3rem" }}>
        <div className="container" style={{ maxWidth: "1000px" }}>
          <h1 style={{ marginTop: "0.8rem" }}>Check how clearly your CV can be read</h1>
          <p className="lead" style={{ maxWidth: "790px" }}>
            Upload a PDF, DOCX, DOC or XML CV for a one-time Job Boost readiness report. It checks common readability, structure and job-ad alignment issues — not your worth or likelihood of being hired.
          </p>


          <form onSubmit={handleSubmit} style={{ marginTop: "1.5rem", padding: "1.5rem", border: "1px solid #d9e2e5", borderRadius: "18px", background: "#fff", display: "grid", gap: "1.2rem" }}>
            <div>
              <label htmlFor="ats-cv-file" style={{ display: "block", fontWeight: 700, marginBottom: "0.45rem" }}>Your CV (PDF, DOCX, DOC or XML, maximum 5MB)</label>
              <input
                id="ats-cv-file"
                name="cv"
                type="file"
                accept=".pdf,.docx,.doc,.xml,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/xml,text/xml"
                onChange={(event) => {
                  setCvFile(event.target.files && event.target.files[0] ? event.target.files[0] : null);
                  setReport(null);
                  setError("");
                }}
                style={{ width: "100%", padding: "0.7rem", border: "1px solid #b9cbd0", borderRadius: "10px", background: "#fff" }}
              />
              {cvFile && <p style={{ margin: "0.45rem 0 0", color: "#38535d", fontSize: "0.94rem" }}>Selected: {cvFile.name} ({Math.ceil(cvFile.size / 1024)}KB)</p>}
            </div>

            <div>
              <label htmlFor="ats-job-description" style={{ display: "block", fontWeight: 700, marginBottom: "0.45rem" }}>Job advert or role description (optional)</label>
              <textarea
                id="ats-job-description"
                name="job_description"
                value={jobDescription}
                onChange={(event) => setJobDescription(event.target.value)}
                maxLength={30000}
                rows={7}
                placeholder="Paste the job advert here to compare relevant terms and phrases. Only add terms that truthfully describe your experience."
                style={{ width: "100%", boxSizing: "border-box", padding: "0.8rem", border: "1px solid #b9cbd0", borderRadius: "10px", font: "inherit", resize: "vertical" }}
              />
            </div>

            <label style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", cursor: "pointer" }}>
              <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} style={{ marginTop: "0.25rem" }} />
              <span>I am allowed to upload this CV and understand that this is an advisory, one-time assessment — not an employer ATS score or a guarantee of an interview.</span>
            </label>

            {error && <div role="alert" style={{ display: "flex", gap: "0.65rem", padding: "0.9rem 1rem", borderRadius: "12px", background: "#fff1ee", color: "#8f2d1a", border: "1px solid #efc0b7" }}><AlertCircle size={20} aria-hidden="true" style={{ flex: "0 0 auto" }} />{error}</div>}

            <div>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? "Checking your CV…" : "Check my CV for free"}
              </button>
            </div>
          </form>

          {report && (
            <section aria-live="polite" style={{ marginTop: "2rem", display: "grid", gap: "1.25rem" }}>
              <article style={{ padding: "1.6rem", borderRadius: "18px", background: "#12384a", color: "#fff" }}>
                <span style={{ fontSize: "0.92rem", fontWeight: 700, letterSpacing: "0.06em" }}>YOUR {report.score_label || "CV READINESS SCORE".toUpperCase()}</span>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.45rem", marginTop: "0.45rem" }}>
                  <strong style={{ fontSize: "3.2rem", lineHeight: 1 }}>{report.ats_readiness_score}</strong><span style={{ fontSize: "1.25rem" }}>/100</span>
                </div>
                <p style={{ margin: "0.85rem 0 0", maxWidth: "760px" }}>{report.disclaimer}</p>
              </article>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.9rem" }}>
                {Object.entries(report.category_scores || {}).map(([key, value]) => {
                  const maximum = report.category_maximums && report.category_maximums[key];
                  const percentage = maximum ? Math.round((value / maximum) * 100) : value;
                  return <article key={key} style={{ padding: "1rem", border: "1px solid #d9e2e5", borderRadius: "14px", background: "#fff" }}>
                    <strong style={{ display: "block", fontSize: "0.96rem" }}>{categoryLabels[key] || key}</strong>
                    <span style={{ display: "block", marginTop: "0.45rem", fontSize: "1.4rem", color: scoreTone(percentage) }}>{value}/{maximum}</span>
                  </article>;
                })}
              </div>

              <article style={{ padding: "1.4rem", border: "1px solid #b9d8cf", borderRadius: "16px", background: "#f2faf7" }}>
                <h2 style={{ fontSize: "1.35rem", marginTop: 0 }}>How to improve this score</h2>
                <p style={{ marginTop: 0 }}>{report.score_guidance || "Use this as a practical guide. Make only accurate changes that reflect your real experience."}</p>
                {improvementPlan.length > 0 ? (
                  <div style={{ display: "grid", gap: "1rem" }}>
                    {improvementPlan.map((item) => (
                      <div key={item.key} style={{ paddingTop: "0.9rem", borderTop: "1px solid #cfe5dd" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "baseline" }}>
                          <strong>{item.title}</strong>
                          <span style={{ whiteSpace: "nowrap", color: "#38535d" }}>{item.current}/{item.maximum}</span>
                        </div>
                        {item.actions && item.actions.length > 0 ? <ul style={{ margin: "0.55rem 0 0", paddingLeft: "1.2rem", display: "grid", gap: "0.4rem" }}>{item.actions.map((action) => <li key={action}>{action}</li>)}</ul> : <p style={{ marginBottom: 0 }}>This area is already meeting the current checks.</p>}
                      </div>
                    ))}
                  </div>
                ) : <p style={{ marginBottom: 0 }}>The current checks did not identify a specific point loss. Review the CV against the job advert and keep every statement accurate.</p>}
              </article>

              <article style={{ padding: "1.25rem", border: "1px solid #d9e2e5", borderRadius: "16px", background: "#fff" }}>
                <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>Revise and check again</h2>
                <ol style={{ margin: "0.45rem 0 0", paddingLeft: "1.25rem", display: "grid", gap: "0.45rem" }}>
                  <li>Make the suggested changes only where they are true for your experience.</li>
                  <li>Save a revised copy of the CV.</li>
                  <li>Use the upload field above to choose the revised file, then select <strong>Check my CV for free</strong>.</li>
                </ol>
                <p style={{ margin: "0.65rem 0 0", color: "#52676e", fontSize: "0.92rem" }}>A higher score is useful only when the content remains accurate and relevant to the role.</p>
              </article>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "1.25rem" }}>
                <article style={{ padding: "1.4rem", border: "1px solid #d9e2e5", borderRadius: "16px", background: "#fff" }}>
                  <h2 style={{ fontSize: "1.3rem", marginTop: 0 }}>Recommended changes</h2>
                  {report.recommended_changes && report.recommended_changes.length ? <ul style={{ paddingLeft: "1.2rem", marginBottom: 0, display: "grid", gap: "0.65rem" }}>{report.recommended_changes.map((item) => <li key={item}>{item}</li>)}</ul> : <p style={{ marginBottom: 0 }}>No immediate changes were flagged by these checks. Read through your CV carefully before applying.</p>}
                </article>

                <article style={{ padding: "1.4rem", border: "1px solid #d9e2e5", borderRadius: "16px", background: "#fff" }}>
                  <h2 style={{ fontSize: "1.3rem", marginTop: 0 }}>What was detected</h2>
                  <ul style={{ padding: 0, margin: 0, listStyle: "none", display: "grid", gap: "0.65rem" }}>
                    <li><strong>Word count:</strong> {report.parsing_preview && report.parsing_preview.word_count}</li>
                    <li><strong>Email detected:</strong> {report.parsing_preview && report.parsing_preview.email_detected ? "Yes" : "No"}</li>
                    <li><strong>Phone detected:</strong> {report.parsing_preview && report.parsing_preview.phone_detected ? "Yes" : "No"}</li>
                    {report.document_quality && report.document_quality.common_typo_candidates && report.document_quality.common_typo_candidates.length > 0 && <li><strong>Review these possible typos:</strong> {report.document_quality.common_typo_candidates.join(", ")}</li>}
                  </ul>
                </article>
              </div>

              {report.job_match && <article style={{ padding: "1.4rem", border: "1px solid #d9e2e5", borderRadius: "16px", background: "#fff" }}>
                <h2 style={{ fontSize: "1.3rem", marginTop: 0 }}>Job-ad alignment</h2>
                {report.job_match.term_alignment_score !== null && <p><strong>{report.job_match.term_alignment_score}%</strong> of extracted job-ad terms were also found in the CV.</p>}
                {report.job_match.missing_terms && report.job_match.missing_terms.length > 0 && <p><strong>Terms to review:</strong> {report.job_match.missing_terms.join(", ")}.</p>}
                <p style={{ marginBottom: 0, color: "#52676e" }}>{report.job_match.note}</p>
              </article>}

            </section>
          )}

        </div>
      </section>
    </main>
  );
}
